import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type {
    AsidePanelConfig,
    AsidePanelKey,
    AsidePanelsState,
    MainPanelConfig,
    MainPanelKey,
    MainPanelsState,
    PanelsStore,
    TabOption,
    TopbarConfig,
    TopbarState,
} from "@/panels/modules/panels/store/panels.store.types";

import { defaultMainPanelConfig, getOtherTab, isTabOption } from "@/panels/modules/panels/store/panels.store.helpers";

export const defaultAsideState: AsidePanelsState = {
    leftSidebar: {},
    rightSidebar: {},
};

export const defaultTopbarState: TopbarState = {
    topbar: {},
};

export const defaultMainState: MainPanelsState = {
    leftTop: defaultMainPanelConfig,
    leftBottom: defaultMainPanelConfig,
    rightTop: defaultMainPanelConfig,
    rightBottom: defaultMainPanelConfig,
};

const defaultState = {
    ...defaultAsideState,
    ...defaultTopbarState,
    ...defaultMainState,
    leftMainPanelDefaultSize: 50,
};

type PanelTabKey = `${MainPanelKey}.${TabOption}`;

const MIRROR_MAP: Partial<Record<PanelTabKey, { panel: MainPanelKey; tab: TabOption }>> = {
    "leftTop.b": { panel: "rightTop", tab: "a" },
    "rightTop.a": { panel: "leftTop", tab: "b" },
    "leftBottom.b": { panel: "rightBottom", tab: "a" },
    "rightBottom.a": { panel: "leftBottom", tab: "b" },
};

export const usePanelsStore = create<PanelsStore>()(
    devtools(
        (set, get) => {
            return {
                ...defaultState,
                setAsidePanelConfig: (panelId: AsidePanelKey, config: Partial<AsidePanelConfig>) =>
                    set(
                        () => {
                            const currentConfig = get()[panelId];
                            const newConfig = { ...currentConfig, ...config };

                            return { [panelId]: newConfig };
                        },
                        false,
                        "setAsidePanelConfig",
                    ),
                setTopbarConfig: (config: Partial<TopbarConfig>) =>
                    set(
                        () => {
                            const currentConfig = get().topbar;
                            const newConfig = { ...currentConfig, ...config };

                            return { topbar: newConfig };
                        },
                        false,
                        "setTopbarConfig",
                    ),
                setMainPanelConfig: (panelId: MainPanelKey, config: Partial<MainPanelConfig>) =>
                    set(
                        () => {
                            const currentConfig = get()[panelId];
                            const newConfig = { ...currentConfig, ...config };

                            return { [panelId]: newConfig };
                        },
                        false,
                        "setMainPanelConfig",
                    ),
                setSelectedTab: (mainPanelKey: MainPanelKey) => (tab: string | null) => {
                    if (!tab || !isTabOption(tab)) return;

                    set(
                        (state) => ({
                            [mainPanelKey]: { ...state[mainPanelKey], selectedTab: tab },
                        }),
                        false,
                        "setSelectedTab",
                    );
                },
                /**
                 * Selects the "Flows" tab on all applicable main panel sections.
                 *
                 * In the spirit of YAGNI, I'm just doing this by tabTitle for now. To make this more robust in the future,
                 * we could add a specific flag to the tab config to indicate the kind of content that may need to be auto-selected.
                 */
                selectFlowsTab: () => {
                    const mainPanelKeys: MainPanelKey[] = ["leftTop", "leftBottom", "rightTop", "rightBottom"];

                    mainPanelKeys.forEach((mainPanelKey) => {
                        const panel = get()[mainPanelKey];
                        const tabs = Object.keys(panel).filter(isTabOption);

                        tabs.forEach((tab) => {
                            if (panel[tab].tabTitle === "Flows" && !panel[tab].isClosed) {
                                get().setSelectedTab(mainPanelKey)(tab);
                            }
                        });
                    });
                },
                setIsMinimized: (mainPanelKey: MainPanelKey) => (tab: string | null) => (isMinimized: boolean) => {
                    if (!tab || !isTabOption(tab)) return;

                    const mirror = MIRROR_MAP[`${mainPanelKey}.${tab}` as PanelTabKey];

                    set(
                        (state) => {
                            const nextState: Partial<PanelsStore> = {
                                [mainPanelKey]: {
                                    ...state[mainPanelKey],
                                    [tab]: { ...state[mainPanelKey][tab], isMinimized },
                                },
                            };

                            if (mirror) {
                                nextState[mirror.panel] = {
                                    ...state[mirror.panel],
                                    [mirror.tab]: { ...state[mirror.panel][mirror.tab], isMinimized },
                                };
                            }

                            return nextState;
                        },
                        false,
                        "setIsMinimized",
                    );
                },
                setIsClosed: (mainPanelKey: MainPanelKey) => (tab: string | null) => (isClosed: boolean) => {
                    if (!tab || !isTabOption(tab)) return;

                    const otherTab = getOtherTab(tab);
                    const tabToSelect = isClosed ? otherTab : tab;

                    set(
                        (state) => {
                            return {
                                [mainPanelKey]: {
                                    ...state[mainPanelKey],
                                    selectedTab: tabToSelect,
                                    [tab]: { ...state[mainPanelKey][tab], isClosed },
                                },
                            };
                        },
                        false,
                        "setIsClosed",
                    );
                },
                setLeftMainPanelDefaultSize: (size: number) => {
                    set(
                        () => ({
                            leftMainPanelDefaultSize: size,
                        }),
                        false,
                        "setLeftMainPanelDefaultSize",
                    );
                },
            };
        },
        {
            store: "panels",
            name: "workspace/panels",
        },
    ),
);
